import { useEffect, useState } from 'react';
import { api } from './client.js';

export interface MaintenanceState {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
}

const DISABLED: MaintenanceState = { enabled: false, message: null, enabledAt: null };

function isSameMaintenanceState(previous: MaintenanceState, next: MaintenanceState): boolean {
  return (
    previous.enabled === next.enabled &&
    previous.message === next.message &&
    previous.enabledAt === next.enabledAt
  );
}

/**
 * Poll the public maintenance flag. Used by the dashboard to render a
 * maintenance screen for non-admin users while an operator works on the
 * system. Backend does NOT enforce the flag — this is a UI-only guard.
 */
export function useMaintenanceFlag(pollMs = 30_000): MaintenanceState {
  const [state, setState] = useState<MaintenanceState>(DISABLED);

  useEffect(() => {
    let cancelled = false;

    const isVisible = () => document.visibilityState === 'visible';

    async function load(): Promise<void> {
      try {
        const res = await api.get<MaintenanceState>('/api/v1/system/maintenance');
        if (!cancelled && res.success && res.data) {
          const next = res.data;
          setState((previous) => (isSameMaintenanceState(previous, next) ? previous : next));
        }
      } catch {
        // Network/transport failure — assume not in maintenance so the app stays usable.
        if (!cancelled) {
          setState((previous) =>
            isSameMaintenanceState(previous, DISABLED) ? previous : DISABLED,
          );
        }
      }
    }

    if (isVisible()) void load();
    const id = window.setInterval(() => {
      if (isVisible()) void load();
    }, pollMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load();
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pollMs]);

  return state;
}
